<template>
    <v-alert
        v-if="!dismissed"
        :class="{ 'info-alert--dismissible': dismissKey }"
        border="left"
        colored-border
        :color="defaultColor"
        :elevation="elevation"
        class="info-alert notice-alert text-body-2 mb-4"
        :style="noticeStyle"
        :type="type"
        :icon="noIcon ? null : icon || 'info'"
    >
        <v-btn v-if="dismissKey" class="info__dismiss" icon small :aria-label="$i18n.locale.startsWith('fr') ? 'Masquer ce message' : 'Dismiss this message'" @click="dismiss"><v-icon small>mdi-close</v-icon></v-btn>
        <div class="info__layout ml-2">
            <div class="info__text">
                <strong v-if="title" class="d-block">{{ title }}</strong>
                <p v-if="summary" class="mt-2 mb-0">{{ summary }}</p>
                <button v-if="collapsible" class="info__toggle" :aria-expanded="expanded" :aria-controls="`info-details-${_uid}`" @click="expanded = !expanded">
                    {{ $i18n.locale.startsWith('fr') ? (expanded ? 'Réduire' : 'En savoir plus') : (expanded ? 'Show less' : 'Learn more') }}
                    <v-icon small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
                <div v-if="collapsible" v-show="expanded" :id="`info-details-${_uid}`" class="mt-3"><slot /></div>
                <slot v-else />
            </div>
            <div v-if="!noButton" class="info__actions">
                <slot name="button" />
            </div>
        </div>
    </v-alert>
</template>

<script>
export default {
    data() {
        let dismissed = false;
        try { dismissed = !!this.dismissKey && localStorage.getItem(this.dismissKey) === '1'; } catch { /* Storage can be unavailable. */ }
        return { dismissed, expanded: false };
    },
    methods: {
        dismiss() {
            this.dismissed = true;
            try { localStorage.setItem(this.dismissKey, '1'); } catch { /* Keep the in-session dismissal. */ }
        },
    },
    props: {
        title: String,
        summary: String,
        collapsible: Boolean,
        dismissKey: String,
        elevation: {
            type: Number,
            default: 0,
        },
        icon: {
            type: String,
            required: false,
        },
        color: {
            type: String,
            required: false,
        },
        type: {
            type: String,
            required: false,
        },
        noIcon: {
            type: Boolean,
            required: false,
            default: false,
        },
        noButton: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        defaultColor() {
            if (this.color) {
                return this.color;
            }
            return this.type && this.type !== "info" ? this.type : "primary";
        },
        noticeStyle() {
            return { "--notice-color": this.$vuetify.theme.currentTheme[this.defaultColor] || this.defaultColor };
        },
    },
};
</script>

<style scoped lang="scss">
.info-alert--dismissible { padding-right: 48px; }
.info__dismiss { position: absolute; top: 8px; right: 8px; }
.info__toggle { margin-top: 8px; font-size: 13px; text-decoration: underline; }
.info__toggle:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; }

.info-alert {
    container-type: inline-size;
    container-name: info;

    ::v-deep .v-alert__wrapper {
        overflow: visible;
    }
}

.info__layout {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
}

.info__text {
    min-width: 0;
    overflow-wrap: break-word;
}

.info__actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    min-width: 0;
    gap: 8px;

    ::v-deep .v-btn {
        width: 100%;
        max-width: 100%;
        white-space: normal;
        min-height: 36px;
        padding-top: 8px;
        padding-bottom: 8px;
    }

    /* Vuetify gives .v-btn__content `flex: 1 0 auto`, so a long label cannot shrink
       and pushes the button past the banner (and the viewport) on narrow screens. */
    ::v-deep .v-btn__content {
        flex: 1 1 auto;
        min-width: 0;
        max-width: 100%;
        white-space: normal;
    }

    /* Split controls: do not stretch every nested v-btn to 100% (overflows the banner). */
    ::v-deep .d-inline-flex .v-btn {
        width: auto;
        max-width: none;
    }

    ::v-deep .d-inline-flex .v-btn__content {
        flex: 1 0 auto;
        white-space: nowrap;
    }

    ::v-deep .d-inline-flex > .v-menu {
        flex: 0 0 auto;
        width: auto;
        display: flex;
        align-self: stretch;

        > .v-btn {
            height: auto;
            align-self: stretch;
        }
    }

    ::v-deep > .d-flex.flex-column {
        gap: 8px;
        width: 100%;
        align-items: stretch;
    }
}

@container info (min-width: 720px) {
    .info__layout {
        flex-direction: row;
        align-items: flex-start;
        gap: 20px;
    }

    .info__text {
        flex: 1 1 auto;
    }

    .info__actions {
        width: auto;
        flex: 0 0 auto;
        align-items: stretch;

        ::v-deep .v-btn {
            width: 100%;
            white-space: nowrap;
        }

        ::v-deep .v-btn__content {
            flex: 1 0 auto;
            white-space: nowrap;
        }

        ::v-deep .d-inline-flex .v-btn {
            width: auto;
        }

        ::v-deep .d-inline-flex > .v-menu {
            flex: 0 0 auto;
            width: auto;
            display: flex;
            align-self: stretch;

            > .v-btn {
                height: auto;
                align-self: stretch;
            }
        }
    }
}

/* Wrapped labels follow the available banner width, including beside the assistant. */
@container info (max-width: 719px) {
    .info__actions ::v-deep .v-btn:not(.v-btn--icon) {
        height: auto;
    }
}
</style>
